/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, type MouseEvent } from 'react';

import { cssMap, jsx } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRouter, useRouterActions } from 'react-resource-router';

import { token } from '@atlaskit/tokens';

import twgLogo from '../../../../assets/twg-logo.svg';
import { clearHash } from '../../../../common/deep-link/clearHash';
import { scrollToTop } from '../../../../common/deep-link/scrollToTop';
import { TWG_MICROSITE_HOME_URL } from '../../../../constants';
import { HOME_ROUTE_NAME } from '../../../../routes';
import { isModifiedClick } from '../../../utils/is-modified-click';
import { messages } from '../messages';

const styles = cssMap({
	logoHomeLink: {
		display: 'flex',
		gap: token('space.100'),
		alignItems: 'center',
		font: token('font.heading.medium'),
		fontFamily: token('font.family.brand.heading'),
		justifySelf: 'start',
		fontWeight: token('font.weight.regular'),
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
		lineHeight: '20px',
		color: 'inherit',
		textDecoration: 'none',
		cursor: 'pointer',
		'&:hover': {
			color: 'inherit',
			textDecoration: 'none',
		},
		'&:active': {
			color: 'inherit',
			textDecoration: 'none',
		},
		'&:visited': {
			color: 'inherit',
			textDecoration: 'none',
		},
	},
	// `logo` and `logoLabel` swap for each other at one breakpoint: below it the
	// wordmark gives way to a logo big enough to carry the brand alone. 52.5rem is
	// not one of the bar's usual breakpoints, it is where the wordmark stops
	// fitting now that the nav buttons render at every width and a signed-in
	// visitor's controls crowd it from the right. One width covers both auth
	// states, so a logged-out visitor loses the wordmark with room to spare.
	logo: {
		width: '16px',
		height: '16px',
		'@media (max-width: 52.5rem)': {
			width: '32px',
			height: '32px',
		},
	},
	logoLabel: {
		'@media (max-width: 52.5rem)': {
			display: 'none',
		},
	},
});

type BrandHomeLinkProps = {
	testId?: string;
};

export const BrandHomeLink = ({ testId = 'brand-home-link' }: BrandHomeLinkProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { push } = useRouterActions();
	const [{ route }] = useRouter();

	// Pushing the home path while already on it changes no route, so
	// `useResetScrollOnRouteChange` never fires and the click looks dead to a
	// visitor partway down the page. Scroll here instead.
	const handleBrandHomeClick = useCallback(
		(event: MouseEvent<HTMLAnchorElement>): void => {
			if (isModifiedClick(event)) {
				return;
			}
			event.preventDefault();

			if (route?.name === HOME_ROUTE_NAME) {
				scrollToTop();
				clearHash();
				return;
			}

			push(TWG_MICROSITE_HOME_URL);
		},
		[push, route?.name],
	);

	return (
		// eslint-disable-next-line @atlaskit/design-system/no-html-anchor -- SPA home navigation needs native link semantics
		<a
			href={TWG_MICROSITE_HOME_URL}
			css={styles.logoHomeLink}
			onClick={handleBrandHomeClick}
			aria-label={formatMessage(messages.teamworkGraphBrand)}
			data-testid={testId}
		>
			<img src={twgLogo} alt="" css={styles.logo} />
			{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
			<span css={styles.logoLabel}>
				<FormattedMessage {...messages.teamworkGraphBrand} />
			</span>
		</a>
	);
};
